import { useStore } from '@nanostores/react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { AnimatePresence, motion } from 'framer-motion';
import { ConnectToGithubButton } from './ConnectToGithubButton';
import { GithubButton } from './GithubButton';
import { MenuDropdownItem, MenuDropdownSeparator } from './Menu/BaseMenuDropdown';
import { HelpDropdownItem } from './Menu/HelpDropdownItem';
import { SubscriptionMenuItem } from './Menu/SubscriptionDropdownItem.client';
import { ThemeDropdownItem } from './Menu/ThemeDropdownItem.client';
import { PublishMenu } from './PublishMenu/PublishMenu.client';
import { StripeButton } from './StripeButton';
import { SupabaseButton } from '~/components/header/SupabaseButton';
import { Avatar } from '~/components/ui/Avatar';
import { Button } from '~/components/ui/Button';
import * as Tooltip from '~/components/ui/Tooltip';
import { useGithubStore } from '~/lib/context/github';
import { menuStore, SettingsPage } from '~/lib/stores/menu';
import { userStore } from '~/lib/stores/user';
import { dropdownAnimationVariants } from '~/utils/animationVariants';

const handleSettingsClick = () => {
  menuStore.openDialog({ type: 'settings', initialPage: SettingsPage.General });
};

const handleSignOutClick = () => {
  menuStore.openDialog({ type: 'confirm-logout' });
};

export function HeaderActionButtons() {
  const user = useStore(userStore.user);
  const activeOrganization = useStore(userStore.getActiveOrganization());

  const showIntegrations = activeOrganization?.organizationSettings?.showIntegrations ?? true;

  const github = useGithubStore();

  const githubConnectedTo = useStore(github.connection);

  return (
    <Tooltip.Provider>
      <Tooltip.Root delayDuration={300}>
        <div className="flex gap-3">
          <div className="flex gap-1 empty:hidden">
            {showIntegrations && user && (
              <>
                <StripeButton />
                <SupabaseButton />
              </>
            )}
          </div>
          {user && (
            <div className="flex gap-1">
              {!githubConnectedTo.repo && <ConnectToGithubButton />}
              {githubConnectedTo.repo && <GithubButton />}
            </div>
          )}
          {showIntegrations && user && (
            <>
              <PublishMenu />
            </>
          )}

          {user && (
            <AnimatePresence>
              <DropdownMenu.Root>
                <DropdownMenu.Trigger asChild>
                  <Button className="text-sm h-8 w-8" variant="ghost" size="md" truncate={false}>
                    <Avatar
                      size="xs"
                      className="size-6"
                      user={{
                        name: user?.name,
                        avatar: user?.avatar,
                      }}
                    />
                  </Button>
                </DropdownMenu.Trigger>
                <DropdownMenu.Content
                  align="end"
                  className="p-1 dark:shadow-lg border border-bolt-elements-borderColor rounded-md overflow-hidden select-none bg-bolt-elements-background-depth-2 min-w-[200px] z-visibility-dropdown text-sm"
                  sideOffset={3}
                >
                  <motion.div initial="close" animate="open" exit="close" variants={dropdownAnimationVariants}>
                    <MenuDropdownItem onClick={handleSettingsClick}>
                      <span className="i-ph:gear-six-duotone w-4 h-4" />
                      Settings
                    </MenuDropdownItem>
                    <HelpDropdownItem />
                    <MenuDropdownSeparator />
                    <SubscriptionMenuItem isLoading={false} />
                    <ThemeDropdownItem isLoading={false} />
                    <MenuDropdownSeparator />
                    <MenuDropdownItem onClick={handleSignOutClick}>
                      <span className="i-lucide:log-out size-4 group-hover:text-bolt-elements-textPrimary" />
                      Sign out
                    </MenuDropdownItem>
                  </motion.div>
                </DropdownMenu.Content>
              </DropdownMenu.Root>
            </AnimatePresence>
          )}
        </div>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
}
